import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();

    this.#setSpeed(this.#calculateDuration());

    // Stock Horizon slowed the marquee to a full stop on pointerenter. lumenate.co's ticker keeps
    // running while hovered, and because this band spans the page width a resting cursor was
    // enough to freeze it, so the hover handlers are not attached.
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
  }

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * Guards against a non-finite duration: `animation-duration: NaNs` is invalid, which drops the
   * animation entirely and leaves the marquee stopped with no way back.
   * @param {number} value
   */
  #setSpeed(value) {
    if (!Number.isFinite(value) || value <= 0) return;

    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  /**
   * Measures how many copies of the item row it takes to fill the track.
   *
   * Stock Horizon read these widths from an IntersectionObserver rooted on this element, and
   * awaited its first callback. That callback never arrives while the tab is in the background
   * (nor does a requestAnimationFrame), so the await in connectedCallback never settled, #setSpeed
   * never ran, `--marquee-speed` stayed unset and the animation shorthand was invalid - a ticker
   * that silently sat still depending on whether its tab had been foregrounded. Reading the rects
   * synchronously is deterministic and works regardless of tab visibility.
   *
   * @returns {Promise<{ numberOfCopies: number, isHorizontalResize: boolean }>}
   */
  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;


    const marqueeWidth = this.getBoundingClientRect().width;
    const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
    this.#marqueeWidth = marqueeWidth;

    const marqueeItemsWidth = marqueeItems[0]?.getBoundingClientRect().width ?? 0;

    // At least one copy: zero would compute a zero-second animation and stop the marquee.
    const numberOfCopies =
      marqueeItemsWidth === 0 ? 1 : Math.max(1, Math.ceil(marqueeWidth / marqueeItemsWidth));

    return { numberOfCopies, isHorizontalResize };
  }

  /**
   * Duration of one full cycle, derived from the distance the wrapper actually travels.
   *
   * The keyframe moves the wrapper by `-50% - gap/2`, which is one copy of the content plus the
   * gap joining it to its clone, so travel is `contentWidth + gap`. Dividing that by a
   * pixels-per-second target holds the visual speed constant across instances and viewport
   * widths. Scaling a fixed duration by `sqrt(copies)` instead, as this did before, tied speed to
   * the width of the logo set: the four press tickers ran at 58, 58, 66 and 69px/s off one
   * setting.
   *
   * Must run after the copies and the clone are in place - adding copies grows the content.
   *
   * @returns {number} seconds, or NaN when the rate or the measurement is unusable
   */
  #calculateDuration() {
    const pixelsPerSecond = Number(this.getAttribute('data-speed'));

    if (!(pixelsPerSecond > 0)) return NaN;

    const { content, wrapper } = this.refs;
    const gap = parseFloat(getComputedStyle(wrapper).columnGap) || 0;

    return (content.getBoundingClientRect().width + gap) / pixelsPerSecond;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    // #queryNumberOfCopies resolves `numberOfCopies`; destructuring `newNumberOfCopies` here read
    // undefined, so every horizontal resize computed a NaN speed and killed the animation.
    const { numberOfCopies: newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    const currentNumberOfCopies = marqueeItems.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(this.#calculateDuration());
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      // The copies exist to fill the track; they carry no new content, so a screen reader
      // should read the press list once rather than once per copy.
      if (clone instanceof HTMLElement) {
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach((focusable) => focusable.setAttribute('tabindex', '-1'));
      }
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
